import {
  ASSIGNMENT_ACTIVE_TYPE,
  ASSIGNMENT_NEW_TYPE,
  ASSIGNMENT_OFFER_ACCEPTED_TYPE,
  ASSIGNMENT_OFFER_NEW_TYPE,
  DIRECT_MESSAGE_TYPE,
  WS_CHAT_TYPE_VALUE,
  WsClient,
  WSEvent
} from 'ws'
import {BehaviorSubject} from 'observables'
import {v4 as UUIDv4} from 'uuid'

export interface EventBusCredentials {
  userId: string
  token: string
  debug?: boolean
}

/**
 * LOW TO HIGH order priorities
 *
 * SYSTEM - should be used from application notification component only
 *
 * LOW, MEDIUM, HEIGHT - should be used from component within a AppPage - preferably use lower priority
 *
 * COMMAND - should be used only when intercepting commands responses from WS
 */
export type DispatcherPriority =
  | 'SYSTEM'
  | 'LOW'
  | 'MEDIUM'
  | 'HIGH'
  | 'COMMAND'

// Function/callback which receives event and should return whether it will consume or process the event
export type Consumable<D, M> = (event: BusEvent<D, M>) => boolean

export type TypedQueue = {
  [key in DispatcherPriority]: {[key: string]: Consumable<unknown, unknown>}
}

// TO WHICH APP IS RELATED THIS EVENT* WS IS RESPONSE FROM WS
export type BusEventScope = 'CUSTOMER' | 'CONTRACTOR' | 'BOTH' | 'WS'

export interface BusEvent<D, M> extends WSEvent<D, M> {
  scope: BusEventScope
}

export interface EventBusRegistry {
  unregister: () => void
}

export class EventBus {
  private ids = 0

  private queue: TypedQueue = {
    COMMAND: {},
    HIGH: {},
    MEDIUM: {},
    LOW: {},
    SYSTEM: {}
  }

  protected connected: boolean = false

  private clientUnsubscribe?: () => void

  constructor(
    private client?: WsClient,
    public credentials?: EventBusCredentials
  ) {}

  public connection: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  )

  // Defer credentials initialization
  public setCredentials(credentials: EventBusCredentials) {
    this.credentials = credentials
  }

  // Defer WS initialization
  public setWsClient(client: WsClient) {
    this.client = client
  }

  /**
   * @throws Error
   */
  async connect() {
    if (!this.client || !this.credentials) {
      throw new Error('EventBus misconfigured')
    }
    if (this.connected) {
      throw new Error('Already connected.')
    }
    const wsUser = {
      id: this.credentials.userId,
      token: this.credentials.token
    }
    if (this.clientUnsubscribe) {
      this.clientUnsubscribe?.()
    }
    this.clientUnsubscribe = this.client.subscribe(
      (e: WSEvent<unknown, unknown>) => {
        this.dispatch(EventBus.wsEventToBusEvent(e))
      }
    )
    this.client.onWSOpen = () => {
      this.connected = true
      this.connection.next(true)
    }
    this.client.onWSClose = () => {
      this.connected = false
      this.connection.next(false)
    }
    await this.client.connect(wsUser, this.credentials.debug)
  }

  disconnect() {
    this.client?.close()
    this.clientUnsubscribe?.()
  }

  register<D, M>(
    fn: Consumable<D, M>,
    priority: DispatcherPriority
  ): EventBusRegistry {
    const id = this.getNextId()
    this.queue[priority][id] = fn as Consumable<unknown, unknown>
    return {
      unregister: () => {
        delete this.queue[priority][id]
      }
    }
  }

  // Directly pass event to all dispatchers
  dispatch(value: BusEvent<unknown, unknown>) {
    const receivers = Object.values(this.queue).flatMap((r) => Object.values(r))
    for (let i = 0; i < receivers.length; i++) {
      if (receivers[i](value)) {
        break
      }
    }
  }

  // Send event to WS
  fire(event: Omit<BusEvent<unknown, unknown>, 'scope'>) {
    this.client?.send(event)
  }

  private static wsEventToBusEvent(
    e: WSEvent<unknown, unknown>
  ): BusEvent<unknown, unknown> {
    const customerSite: string[] = [
      ASSIGNMENT_ACTIVE_TYPE,
      ASSIGNMENT_OFFER_NEW_TYPE
    ]
    const contractorSite: string[] = [
      ASSIGNMENT_NEW_TYPE,
      ASSIGNMENT_OFFER_ACCEPTED_TYPE
    ]
    let scope: BusEventScope = 'BOTH'
    if (customerSite.includes(e.type)) {
      scope = 'CUSTOMER'
    } else if (contractorSite.includes(e.type)) {
      scope = 'CONTRACTOR'
    }
    if (
      e.type === WS_CHAT_TYPE_VALUE &&
      (e.data as {type: string})?.type !== DIRECT_MESSAGE_TYPE
    ) {
      scope = 'WS'
    }
    return {...e, scope: scope}
  }

  private getNextId(): number {
    return this.ids++
  }

  public generateUUID() {
    return UUIDv4()
  }
}
